import { useMutation, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { graphqlClient } from '@/lib/graphql';

// Types
export interface SubmitInviteRequestInput {
  email: string;
}

export interface SubmitInviteRequestPayload {
  success: boolean;
  message: string;
}

export interface Invite {
  token: string;
  email: string;
  sentAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  isExpired: boolean;
  isRedeemed: boolean;
}

export interface RedeemInviteInput {
  token: string;
}

export interface RedeemInvitePayload {
  success: boolean;
  message: string;
}

export interface VerifyEmailInput {
  token: string;
}

export interface VerifyEmailPayload {
  success: boolean;
  message: string;
}

// Queries
const VALIDATE_INVITE = gql`
  query ValidateInvite($token: String!) {
    validateInvite(token: $token) {
      token
      email
      sentAt
      expiresAt
      redeemedAt
      isExpired
      isRedeemed
    }
  }
`;

// Mutations
const SUBMIT_INVITE_REQUEST = gql`
  mutation SubmitInviteRequest($input: SubmitInviteRequestInput!) {
    submitInviteRequest(input: $input) {
      success
      message
    }
  }
`;

const REDEEM_INVITE = gql`
  mutation RedeemInvite($input: RedeemInviteInput!) {
    redeemInvite(input: $input) {
      success
      message
    }
  }
`;

const VERIFY_EMAIL = gql`
  mutation VerifyEmail($input: VerifyEmailInput!) {
    verifyEmail(input: $input) {
      success
      message
    }
  }
`;

// Hooks
export function useSubmitInviteRequest() {
  return useMutation({
    mutationFn: async (input: SubmitInviteRequestInput) => {
      const data = await graphqlClient.request<{
        submitInviteRequest: SubmitInviteRequestPayload;
      }>(SUBMIT_INVITE_REQUEST, { input });
      return data.submitInviteRequest;
    },
  });
}

export function useValidateInvite(token: string) {
  return useQuery({
    queryKey: ['validateInvite', token],
    queryFn: async () => {
      const data = await graphqlClient.request<{
        validateInvite: Invite | null;
      }>(VALIDATE_INVITE, { token });
      return data.validateInvite;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useRedeemInvite() {
  return useMutation({
    mutationFn: async (input: RedeemInviteInput) => {
      const data = await graphqlClient.request<{
        redeemInvite: RedeemInvitePayload;
      }>(REDEEM_INVITE, { input });
      return data.redeemInvite;
    },
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: async (input: VerifyEmailInput) => {
      const data = await graphqlClient.request<{
        verifyEmail: VerifyEmailPayload;
      }>(VERIFY_EMAIL, { input });
      return data.verifyEmail;
    },
  });
}
